import { useState } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'

import classnames from 'classnames'
import invariant from 'tiny-invariant'

import { useFoodMutation } from '~/api-client/food_api'
import { chatGetOrCreateChatSpace } from '~/api-client/food_api/chat'
import { IntroductionLetter, User } from '~/api-definition/definition'
import { Button } from '~/components/action/Button'
import { Modal } from '~/components/action/Modal'
import { ChevronRightIcon, CloseIcon } from '~/components/icons'
import { formatDateTime } from '~/libs/date'
import { useAccessToken } from '~/provider/SessionProvider'

import { IntroductionLetterUserInfo } from './IntroductionLetterUserInfo'

export const IntroductionLetterListItem: React.FC<{
  letter: IntroductionLetter
  toUser?: User
  hideSentAt?: boolean
  hideBodyText?: boolean
  displayToUser?: boolean
  displayEditButton?: boolean
  displayDeleteButton?: boolean
  displayControls?: boolean
  refreshIntroductionLetters?: () => void
}> = (props) => {
  const router = useRouter()
  const accessToken = useAccessToken()
  const toUser = props.toUser ?? props.letter.toUser
  const user = props.displayToUser ? toUser : props.letter.fromUser

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [showRequestMessageConfirmation, setShowRequestMessageConfirmation] =
    useState(false)

  const approveIntroductionLetter = useFoodMutation(
    '/user/approve_introduction_letter',
    {
      onSuccess: () => {
        props.refreshIntroductionLetters?.()
      },
    }
  )
  const cancelIntroductionLetter = useFoodMutation(
    '/user/cancel_introduction_letter',
    {
      onSuccess: () => {
        props.refreshIntroductionLetters?.()
      },
    }
  )
  const deleteIntroductionLetter = useFoodMutation(
    '/user/delete_introduction_letter',
    {
      onSuccess: () => {
        props.refreshIntroductionLetters?.()
      },
    }
  )

  return (
    <>
      <Modal
        isOpen={showDeleteConfirmation}
        setIsOpen={setShowDeleteConfirmation}
        title="本当に紹介コメントを削除しますか?"
        body={<p>紹介コメントを削除すると元に戻すことはできません。</p>}
        actionButtons={
          <div className="grid grid-cols-2 mt-8 gap-x-4">
            <Button
              type="button"
              className="w-full bg-gray"
              variant="functionWeak"
              shrink
              onClick={async () => {
                setShowDeleteConfirmation(false)
              }}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              shrink
              onClick={async () => {
                await deleteIntroductionLetter({
                  introductionLetterId: props.letter.introductionLetterId,
                })
                setShowDeleteConfirmation(false)
                props.refreshIntroductionLetters?.()
              }}
            >
              OK
            </Button>
          </div>
        }
      />
      <Modal
        isOpen={showRequestMessageConfirmation}
        setIsOpen={setShowRequestMessageConfirmation}
        title={
          <>
            個別チャットで
            <br />
            修正依頼のメッセージを送りますか?
          </>
        }
        actionButtons={
          <div className="grid grid-cols-2 mt-8 gap-x-4">
            <Button
              type="button"
              className="w-full bg-gray"
              variant="functionWeak"
              shrink
              onClick={async () => {
                setShowRequestMessageConfirmation(false)
              }}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              shrink
              onClick={async () => {
                invariant(accessToken != null)
                invariant(props.letter.fromUser != null)
                const space = await chatGetOrCreateChatSpace({
                  accessToken,
                  pairUserId: props.letter.fromUser.userId,
                })
                await router.push(`/chat_spaces/${space.space.chatSpaceId}`)
              }}
            >
              メッセージを送る
            </Button>
          </div>
        }
      />
      <div
        className="flex justify-between gap-3 items-center"
        role="button"
        onClick={() => {
          if (
            props.hideBodyText &&
            props.letter.bodyText != null &&
            props.letter.bodyText.length > 0
          ) {
            router.push(
              `/foodies/${toUser?.userName}/introduction_letters/${props.letter.introductionLetterId}`
            )
          }
        }}
      >
        <div className="w-full">
          <div className="flex justify-between">
            {!props.hideSentAt && (
              <div className="text-[#999] text-xs">
                {formatDateTime(
                  { sec: props.letter.sentAt },
                  { formateDateOpts: { sep: 'slash' } }
                )}
              </div>
            )}
            {props.displayDeleteButton != null && (
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirmation(true)
                }}
              >
                <CloseIcon className="fill-function w-[14px] h-[14px]" />
              </button>
            )}
          </div>

          <div
            className={classnames(
              'text-sm mt-1/2 mb-3 whitespace-pre-wrap font-bold',
              !props.hideBodyText && 'font-bold'
            )}
          >
            {props.letter.title}
          </div>

          {!props.hideBodyText &&
            props.letter.bodyText != null &&
            props.letter.bodyText.length > 0 && (
              <div className="text-sm mt-3 mb-4">{props.letter.bodyText}</div>
            )}

          {user != null && <IntroductionLetterUserInfo user={user} />}
        </div>
        {props.hideBodyText &&
          props.letter.bodyText != null &&
          props.letter.bodyText.length > 0 && (
            <ChevronRightIcon className="fill-function h-3" />
          )}
      </div>
      {props.displayEditButton && (
        <div className="flex gap-3 justify-center mt-4">
          <Link
            href={`/foodies/${toUser?.userName}/introduction_letters/${props.letter.introductionLetterId}/edit`}
          >
            <Button variant="function">編集する</Button>
          </Link>
        </div>
      )}

      {props.letter.state === 'pending' && props.displayControls != null && (
        <div className="flex gap-3 justify-center mt-4">
          <Button
            variant="functionWeak"
            onClick={() => setShowRequestMessageConfirmation(true)}
          >
            修正依頼
          </Button>
          <Button
            variant="function"
            onClick={() => {
              approveIntroductionLetter({
                introductionLetterId: props.letter.introductionLetterId,
              })
            }}
          >
            承認
          </Button>
        </div>
      )}
      {props.letter.state === 'published' && props.displayControls != null && (
        <div className="flex gap-3 justify-center mt-4">
          <Button
            variant="functionWeak"
            onClick={() => {
              cancelIntroductionLetter({
                introductionLetterId: props.letter.introductionLetterId,
              })
            }}
          >
            承認待ちに戻す
          </Button>
        </div>
      )}
      <div className="border-b border-dotted border-gray my-4" />
    </>
  )
}
